import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';

const facility = {
  slug: 'packaging',
  name: 'Pak Pro Bangladesh Ltd.',
  tagline: 'End-to-End Export Packaging Solutions',
  description: 'Complete packaging manufacturing for the apparel export industry — poly bags, cartons, inner boxes, master cartons, hang tags, and brand-specific retail packaging that protect your garments and elevate your brand.',
  longDescription: 'Pak Pro Bangladesh Ltd. is the Beaumonde Tex group\'s dedicated packaging facility. We manufacture every packaging component an apparel export order needs — from poly bags and inner boxes to branded retail packaging and master cartons. Our facility handles both standard export packaging and fully customized brand experiences with custom printing, embossing, foil stamping, and sustainable material options. With 12 production lines, 200+ skilled personnel, and a monthly output of 100 million units, we ensure every garment reaches its destination in perfect condition — while presenting your brand exactly the way you want it.',
  heroImage: 'https://readdy.ai/api/search-image?query=Modern%20packaging%20manufacturing%20facility%20with%20organized%20production%20line%2C%20printed%20cartons%20and%20packaging%20samples%20on%20display%2C%20clean%20industrial%20workspace%2C%20bright%20lighting%2C%20professional%20product%20photography%2C%20warm%20neutral%20tones%2C%20high%20quality%20commercial%20aesthetic&width=1800&height=800&seq=packaging-hero-individual&orientation=landscape',
  capacityStats: [
    { value: '55,000', label: 'Sq. Ft. Facility', icon: 'ri-building-4-line' },
    { value: '200+', label: 'Skilled Workforce', icon: 'ri-team-line' },
    { value: '12', label: 'Production Lines', icon: 'ri-settings-line' },
    { value: '100M', label: 'Monthly Units', icon: 'ri-stack-line' },
    { value: '6', label: 'Printing Presses', icon: 'ri-printer-line' },
    { value: '4', label: 'Corrugation Lines', icon: 'ri-layout-row-line' },
  ],
  machines: [
    { name: 'Corrugation Lines', qty: '4', spec: 'BHS & Mitsubishi, B/C/E flute', desc: 'Single, double, and triple wall corrugated board production for cartons of all strengths.' },
    { name: 'Flexo Printing Presses', qty: '6', spec: 'Bobst & Göpfert, up to 4 colors', desc: 'High-speed flexographic printing for carton branding, logos, and handling instructions.' },
    { name: 'Die-Cutting Machines', qty: '5', spec: 'Bobst & Shanghai Eternal', desc: 'Precision die-cutting for carton blanks, inserts, and custom-shaped packaging components.' },
    { name: 'Poly Bag Extruders', qty: '4', spec: 'W&H & Reifenhäuser, LDPE/HDPE', desc: 'Blown film extrusion for poly bags with inline printing, cutting, and sealing.' },
    { name: 'Offset Printing Presses', qty: '2', spec: 'Heidelberg Speedmaster, 5-color', desc: 'Premium offset printing for hang tags, retail boxes, and high-end brand packaging.' },
    { name: 'Foil Stamping Machines', qty: '3', spec: 'Bobst & Yawa, hot & cold foil', desc: 'Metallic foil stamping, embossing, and debossing for premium brand presentation.' },
  ],
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=Corrugated%20carton%20manufacturing%20production%20line%20with%20organized%20workflow%2C%20modern%20machinery%2C%20clean%20factory%20floor%2C%20bright%20industrial%20lighting%2C%20professional%20packaging%20industry%20photography&width=700&height=500&seq=packaging-gallery-01&orientation=landscape',
      alt: 'Carton Production Line',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Flexographic%20printing%20press%20in%20operation%20producing%20printed%20cartons%2C%20vibrant%20colors%2C%20modern%20clean%20facility%2C%20professional%20print%20industry%20photography%2C%20bright%20lighting&width=700&height=500&seq=packaging-gallery-02&orientation=landscape',
      alt: 'Flexo Printing Floor',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Poly%20bag%20manufacturing%20line%20with%20extrusion%20and%20sealing%20equipment%2C%20organized%20production%20floor%2C%20clean%20industrial%20environment%2C%20bright%20lighting%2C%20professional%20photography&width=700&height=500&seq=packaging-gallery-03&orientation=landscape',
      alt: 'Poly Bag Production',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Quality%20control%20station%20with%20packaging%20samples%20laid%20out%20for%20inspection%2C%20measurement%20tools%2C%20organized%20clean%20workspace%2C%20professional%20product%20photography&width=700&height=500&seq=packaging-gallery-04&orientation=landscape',
      alt: 'Quality Inspection',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Custom%20retail%20packaging%20display%20with%20branded%20boxes%20tissue%20paper%20and%20hang%20tags%2C%20elegant%20product%20presentation%20on%20clean%20surface%2C%20professional%20commercial%20photography%2C%20soft%20studio%20lighting&width=700&height=500&seq=packaging-gallery-05&orientation=landscape',
      alt: 'Branded Retail Packaging',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Organized%20packaging%20warehouse%20with%20stacked%20cartons%20and%20packaged%20materials%20on%20pallet%20racks%2C%20systematic%20inventory%2C%20clean%20bright%20storage%20facility%2C%20professional%20industrial%20photography&width=700&height=500&seq=packaging-gallery-06&orientation=landscape',
      alt: 'Finished Goods Warehouse',
    },
  ],
  capabilities: [
    { icon: 'ri-shopping-bag-line', title: 'Poly Bags', desc: 'Plain, printed, and biodegradable poly bags in all sizes — with or without adhesive flaps.' },
    { icon: 'ri-archive-line', title: 'Cartons', desc: 'Single, double, and triple wall corrugated cartons in custom sizes with branded printing.' },
    { icon: 'ri-inbox-line', title: 'Inner Boxes', desc: 'Inner boxes, dividers, and partitions for organized garment presentation inside master cartons.' },
    { icon: 'ri-price-tag-3-line', title: 'Hang Tags', desc: 'Custom hang tags with string, safety pin, or plastic attachment — with variable data printing.' },
    { icon: 'ri-gift-line', title: 'Retail Boxes', desc: 'Branded retail boxes with custom printing, embossing, foil stamping, and premium finishing.' },
    { icon: 'ri-sticky-note-line', title: 'Labels & Stickers', desc: 'Barcode labels, price stickers, size stickers, and RFID tags for inventory tracking.' },
    { icon: 'ri-file-paper-line', title: 'Tissue Paper', desc: 'Plain and printed tissue paper in various GSM weights for garment wrapping and presentation.' },
    { icon: 'ri-leaf-line', title: 'Eco Options', desc: 'FSC-certified cartons, recycled poly bags, biodegradable materials, and soy-based inks.' },
  ],
};

export default function PackagingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] md:h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${facility.heroImage})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <button
            onClick={() => navigate('/sister-concerns')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors duration-200 mb-5 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            All Sister Concerns
          </button>
          <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold font-heading mb-4">
            Packaging
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {facility.name}
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-heading font-medium mb-4">{facility.tagline}</p>
          <p className="max-w-[650px] text-sm md:text-base text-white/65 leading-relaxed">{facility.description}</p>
        </div>
      </section>

      {/* Capacity Dashboard */}
      <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {facility.capacityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-accent-50 mb-2">
                  <i className={`${stat.icon} text-accent-500 text-base`}></i>
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-3/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">About the Facility</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                Protect Your Product. Present Your Brand.
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{facility.longDescription}</p>
            </div>
            <div className="lg:w-2/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0]">
                <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-accent-500"></i>
                  Machine & Equipment
                </h3>
                <div className="space-y-4">
                  {facility.machines.map((m) => (
                    <div key={m.name} className="border-b border-[#E6EAF0] pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-foreground-800">{m.name}</p>
                        <span className="text-xs font-bold text-accent-500 whitespace-nowrap">{m.qty} units</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 mb-1">{m.spec}</p>
                      <p className="text-xs text-foreground-400 leading-snug">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">Inside the Facility</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Photo Tour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facility.gallery.map((img, i) => (
              <div
                key={img.alt}
                className="rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="bg-white px-4 py-3">
                  <p className="text-sm font-medium text-foreground-700">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-4">What We Make</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Packaging Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facility.capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-accent-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mb-4">
                  <i className={`${cap.icon} text-accent-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{cap.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-accent-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Ready to Package Your Brand?</h2>
          <p className="text-white/80 max-w-[500px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            From poly bags to premium retail boxes, we manufacture packaging that protects your product and elevates your brand experience.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-accent-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/sister-concerns')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              All Sister Concerns
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}